'use client';

import { useSuspenseQuery } from '@apollo/client/react';
import { FC, PropsWithChildren, useMemo } from 'react';

import { GetSavedPublicationsQuery } from '@/.gql/graphql';
import { GET_SAVED_PUBLICATIONS } from '@/.graphql';

import { createCustomContext } from './context';

interface ISavedPublicationsProps {
  savedPublications: GetSavedPublicationsQuery['getSavedPublications']['data'];
  hasMoreSavedPublications: boolean;
  isLoadingSavedPublications: boolean;
  loadMoreSavedPublicationss: () => void;
}

const [SavedPublicationsContext, useSavedPublicationsContext] =
  createCustomContext<ISavedPublicationsProps>({
    name: 'SavedPublicationsContext',
    strict: false,
  });

export { useSavedPublicationsContext };

export const SavedPublicationsProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: imageData,
    fetchMore: fetchMoreSavedPublicationss,
    networkStatus: imageNetworkStatus,
  } = useSuspenseQuery(GET_SAVED_PUBLICATIONS, {
    variables: {
      cursor: null,
    },
  });

  const savedPublications = useMemo(
    () => imageData?.getSavedPublications?.data || [],
    [imageData],
  );

  const hasMoreSavedPublications = !!imageData?.getSavedPublications.nextCursor;

  const isLoadingSavedPublications = imageNetworkStatus === 3;
  const loadMoreSavedPublicationss = useMemo(
    () => () => {
      if (!hasMoreSavedPublications || isLoadingSavedPublications) {
        return;
      }

      return fetchMoreSavedPublicationss({
        variables: {
          cursor: imageData?.getSavedPublications.nextCursor,
        },
      });
    },
    [
      hasMoreSavedPublications,
      isLoadingSavedPublications,
      imageData,
      fetchMoreSavedPublicationss,
    ],
  );

  const contextValue = useMemo(
    () => ({
      savedPublications,
      hasMoreSavedPublications,
      loadMoreSavedPublicationss,
      isLoadingSavedPublications,
    }),
    [
      savedPublications,
      hasMoreSavedPublications,
      loadMoreSavedPublicationss,
      isLoadingSavedPublications,
    ],
  );

  return (
    <SavedPublicationsContext value={contextValue}>
      {children}
    </SavedPublicationsContext>
  );
};
